/**
 * ComparisonEvidenceModal — click-through modal shown when user taps a rating cell.
 *
 * Renders the per-cell Public Summary verbatim from the Evidence Register,
 * plus a list of cited source links. Internal-only sources show as plain text
 * (no link); pending sources show with a "Source pending" label.
 *
 * Closes via: clicking the backdrop, the close button, or pressing Escape.
 */
import React, { useEffect } from 'react';

export const ComparisonEvidenceModal = ({ cell, competitorName, rowLabel, onClose, isMobile = false }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!cell) return null;

  const ratingColour =
    cell.colour === 'green' ? 'bg-emerald-500' :
    cell.colour === 'red' ? 'bg-red-500' :
    'bg-yellow-500';
  const ratingTextColour =
    cell.colour === 'green' ? 'text-emerald-300' :
    cell.colour === 'red' ? 'text-red-300' :
    'text-yellow-300';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      data-testid="comparison-evidence-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="evidence-modal-title"
    >
      <div className="absolute inset-0 bg-black/70" onClick={onClose} data-testid="comparison-evidence-backdrop" />
      <div
        className={`relative z-10 ${isMobile ? 'mx-3 w-[94vw]' : 'mx-4 max-w-2xl w-full'} rounded-2xl p-6`}
        style={{
          backgroundColor: 'rgba(20, 25, 35, 0.98)',
          backdropFilter: 'blur(16px)',
          border: '1px solid rgba(255,255,255,0.12)',
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-5 text-gray-400 hover:text-white transition-colors text-xl"
          data-testid="comparison-evidence-close"
          aria-label="Close evidence panel"
        >
          &times;
        </button>

        {/* Header */}
        <div className="mb-4 pr-8">
          <p className="text-xs uppercase tracking-widest text-gray-500 mb-1">{competitorName}</p>
          <h3 id="evidence-modal-title" className="text-lg font-bold text-white" data-testid="comparison-evidence-row-label">{rowLabel}</h3>
        </div>

        {/* Rating chip + display value */}
        <div className="flex items-center gap-3 mb-4">
          <span className={`inline-block w-3 h-3 rounded-full ${ratingColour} shadow-md`} />
          <span className={`text-sm font-semibold ${ratingTextColour}`} data-testid="comparison-evidence-display">
            {cell.display}
          </span>
        </div>

        {/* Public Summary — verbatim from Evidence Register */}
        <p className="text-sm text-gray-200 leading-relaxed mb-5" data-testid="comparison-evidence-summary">
          {cell.summary}
        </p>

        {/* Source list */}
        {cell.sources && cell.sources.length > 0 && (
          <div className="border-t border-gray-700/50 pt-4">
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Sources</p>
            <ul className="space-y-2">
              {cell.sources.map((src, i) => (
                <li key={`${src.id || i}`} className="text-sm">
                  {src.url ? (
                    <a
                      href={src.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-400 hover:text-cyan-300 underline underline-offset-2"
                      data-testid={`comparison-evidence-source-link-${i}`}
                    >
                      {src.title}
                    </a>
                  ) : (
                    <span className="text-gray-400 italic" data-testid={`comparison-evidence-source-placeholder-${i}`}>
                      {src.title}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default ComparisonEvidenceModal;
